"use client";

import {
  type MouseEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
  type FC,
  type Ref,
} from "react";
import { mergeClasses, padNumber, toHumanCase } from "@huub-web/utils";
import {
  Button,
  Container,
  type ContainerProps,
  Grid,
  Heading,
  mergeRefs,
  Message,
  ProgressBar,
  Spacing,
  SSRWrapper,
  useDelayedState,
  type ClassNameProp,
  Flex,
  Text,
  Icon,
  useIntersectionObserver,
  IconButton,
  Tooltip,
  useUuid,
} from "@huub-web/ui";
import { useTranslation } from "react-i18next";
import { FiltersContent } from "../MapControlCard";

import {
  type HotspotRecord,
  MapboxMapView,
  type MapboxMapViewHandle,
  type MapboxMapViewProps,
  getMapboxDirections,
  addRouteLayer,
  removeRouteLayer,
  isWebGLSupported,
} from "../MapboxMapView";
import { useClusteredHotspots } from "../../hooks/useClusteredHotspots";
import { useContentMenuSection } from "../../hooks/useContentMenuSection";
import { Section } from "../Section";

import { amenitiesGridStyle, mapContainer } from "./NeighbourhoodAnalysis.css";
import { AmenityCard } from "../AmenityCard";
import { AnalysisIndicator } from "../AnalysisIndicator";
import { SummaryLoading } from "../Analysis/components/SummaryLoading";
import { createCircleGeoJSON } from "./utils/createCircleGeoJSON";
import { getAmenityHotspot } from "./utils/getAmenityHotspot";
import { getMainHotspot } from "./utils/getMainHotspot";
import {
  analysisSectionIllustrations,
  type AnalysisSectionIndex,
} from "../Analysis/types/AnalysisSections";
import type { Amenity } from "../../types";

/**
 * Selected route state containing amenity and route details
 */
type SelectedRouteState = {
  amenity: Amenity & { center: [number, number] };
  distance: number;
  duration: number;
} | null;

export type NeighbourhoodAnalysisProps = ClassNameProp & {
  /**
   * Index of the section
   **/
  index?: AnalysisSectionIndex;
  /**
   * Address of the neighbourhood
   **/
  address: string;
  /**
   * Center coordinates [longitude, latitude] for Mapbox
   **/
  center: [number, number];
  /**
   * Zoom level (default: 14)
   **/
  zoom?: number;
  /**
   * Optional GeoJSON data to display on the map
   **/
  geoJSON?: GeoJSON.FeatureCollection;
  /**
   * Mapbox access token
   **/
  accessToken?: string;
  /**
   * Title of the section
   **/
  title?: string;
  /**
   * Name of the neighbourhood
   **/
  name?: string;
  /**
   * Description of the neighbourhood
   **/
  description?: string;
  /**
   * Amenities of the neighbourhood
   **/
  amenities: Amenity[];
  /**
   * Container size
   **/
  container?: ContainerProps["size"];
  /**
   * Whether the component is loading
   **/
  loading?: boolean;
  /**
   * Callback when the "View All" button is clicked
   **/
  onViewAll?: () => void;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * NeighbourhoodAnalysis component displays a Mapbox map
 * with optional GeoJSON data overlay
 */
export const NeighbourhoodAnalysis: FC<NeighbourhoodAnalysisProps> = ({
  index,
  address,
  center,
  zoom = 14,
  accessToken,
  title = "In de buurt",
  name,
  description,
  amenities,
  container = "marketing",
  loading,
  className,
  onViewAll,
  ref,
}) => {
  const internalRef = useContentMenuSection<HTMLDivElement>({
    id: "neighbourhood",
    label: title,
    index,
  });

  const { t } = useTranslation();

  const id = `analysisSection${index}`;
  const uuid = useUuid();

  const webGLSupported = isWebGLSupported();

  const mapInstanceRef = useRef<MapboxMapViewHandle>(null);

  const [isMapLoaded, setIsMapLoaded] = useDelayedState<boolean>(false, 1000);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [currentZoom, setCurrentZoom] = useState<number>(zoom);
  const [selectedRoute, setSelectedRoute] = useState<SelectedRouteState>(null);

  const { ref: intersectionRef, isIntersecting: isMapIntersecting } =
    useIntersectionObserver<HTMLDivElement>(undefined, {
      triggerOnce: true,
      threshold: 0.2,
    });

  const handleReady = useCallback(() => {
    setIsMapLoaded(true);
  }, [setIsMapLoaded]);

  const handleViewportChange = useCallback(() => {
    const currentZoomLevel = mapInstanceRef.current?.getZoom();
    if (currentZoomLevel !== null && currentZoomLevel !== undefined) {
      setCurrentZoom(currentZoomLevel);
    }
  }, []);

  const handleClusterClick = (
    e: MouseEvent<HTMLElement>,
    lat: number,
    lng: number,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const currentZoomLevel = mapInstanceRef.current?.getZoom() ?? 15;
    mapInstanceRef.current?.flyTo({
      center: [lng, lat],
      zoom: Math.max(currentZoomLevel + 1, 17),
      duration: 1500,
    });
  };

  const handleAmenityClick = async (
    amenity: Amenity & { center: [number, number] },
  ) => {
    const map = mapInstanceRef.current?.getMap();
    if (!map || !accessToken) {
      return;
    }

    const amenityId = `${amenity.name}-${amenity.center[0]}-${amenity.center[1]}`;
    const selectedAmenityId = selectedRoute
      ? `${selectedRoute.amenity.name}-${selectedRoute.amenity.center[0]}-${selectedRoute.amenity.center[1]}`
      : null;

    // If clicking the same amenity, clear the route
    if (selectedAmenityId === amenityId) {
      removeRouteLayer(map);
      setSelectedRoute(null);
      return;
    }

    const result = await getMapboxDirections({
      accessToken,
      origin: center,
      destination: amenity.center,
    });

    if (result) {
      addRouteLayer({
        map,
        route: result.route,
        origin: center,
        destination: amenity.center,
      });
      setSelectedRoute({
        amenity,
        distance: result.distance,
        duration: result.duration,
      });

      // Zoom to route bounds
      const coordinates = result.route.geometry.coordinates;
      const lngs = coordinates.map((c) => c[0]);
      const lats = coordinates.map((c) => c[1]);
      const bounds: [[number, number], [number, number]] = [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ];
      mapInstanceRef.current?.fitBounds(bounds, {
        padding: 120,
        duration: 1000,
      });
    }
  };

  const categories: { label: string; id: string }[] = (() => {
    const uniqueCategories = Array.from(
      new Set(
        amenities
          .map((amenity) => amenity.category)
          .filter((category): category is string => category !== undefined),
      ),
    );
    return uniqueCategories.sort().map((category) => ({
      id: category,
      label: toHumanCase(category),
    }));
  })();

  const getCategoryCount = (categoryId: string) =>
    amenities.filter((amenity) => amenity.category === categoryId).length;

  const oneAmenityPerCategory = (() => {
    const seenCategories = new Set<string>();
    return amenities.filter((amenity) => {
      if (!amenity.category || seenCategories.has(amenity.category)) {
        return false;
      }
      seenCategories.add(amenity.category);
      return true;
    });
  })();

  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((c) => c !== categoryId)
        : [...prev, categoryId],
    );
  };

  /**
   * Handle back button click to clear route and return to filters
   */
  const handleBack = () => {
    const map = mapInstanceRef.current?.getMap();
    if (map) {
      removeRouteLayer(map);
    }
    setSelectedRoute(null);
  };

  const circleGeoJSON = useMemo<GeoJSON.FeatureCollection>(
    () => createCircleGeoJSON({ center, radiusInMeters: 1000 }),
    [center],
  );

  const selectedAmenityId = selectedRoute
    ? `${selectedRoute.amenity.name}-${selectedRoute.amenity.center[0]}-${selectedRoute.amenity.center[1]}`
    : null;

  const hotspots: HotspotRecord[] = (() => {
    // When route is active, only show main marker and active amenity
    if (selectedRoute) {
      return [
        getAmenityHotspot({
          sessionID: id,
          address,
          amenity: selectedRoute.amenity,
          time: selectedRoute.duration,
          index: 0,
          active: true,
          onClick: () => handleAmenityClick(selectedRoute.amenity),
          onClose: handleBack,
        }),
        getMainHotspot({
          id: uuid,
          address,
          name,
          center,
          loading,
          loadingLabel: t("analysis.loading"),
          active: true,
          closable: false,
        }),
      ];
    }

    // When no route, show all filtered amenities
    return [
      ...(amenities && amenities.length > 0 && !loading
        ? amenities
            .filter(
              (
                amenity,
              ): amenity is typeof amenity & { center: [number, number] } =>
                amenity.center !== undefined &&
                (selectedCategories.length === 0 ||
                  (amenity.category !== undefined &&
                    selectedCategories.includes(amenity.category))),
            )
            .map((amenity, index) => {
              const amenityID = `${amenity.name}-${amenity.center[0]}-${amenity.center[1]}`;

              return getAmenityHotspot({
                sessionID: id,
                address,
                amenity,
                index,
                onClick: () => handleAmenityClick(amenity),
                active: selectedAmenityId === amenityID,
              });
            })
        : []),
      getMainHotspot({
        id: uuid,
        address,
        name,
        center,
        loading,
        loadingLabel: t("analysis.loading"),
      }),
    ];
  })();

  // Include route state in resetKey to clear cached hotspots when route becomes active/inactive
  const clusterResetKey = {
    categories: selectedCategories,
    hasRoute: !!selectedRoute,
  };

  const { clusteredHotspots } = useClusteredHotspots({
    sessionID: id,
    hotspots,
    currentZoom,
    onClusterClick: handleClusterClick,
    label: "voorzieningen",
    resetKey: clusterResetKey,
  });

  const controlsConfig: MapboxMapViewProps["controlsConfig"] = useMemo(
    () => ({
      zoom: true,
      mode: false,
      scrollZoom: false,
    }),
    [],
  );

  if (!accessToken) {
    return (
      <Section id={id}>
        <Container size={container}>
          <Message variant="error" body="Access token is required for MapBox" />
        </Container>
      </Section>
    );
  }

  return (
    <Section
      id={id}
      ref={mergeRefs(ref, internalRef, intersectionRef)}
      edge="none"
    >
      {/* <pre>{JSON.stringify({ amenities }, null, 2)}</pre> */}

      <Container size={container}>
        {index !== undefined && (
          <AnalysisIndicator
            index={index}
            illustration={analysisSectionIllustrations[index]}
          />
        )}
        <Heading
          title={title}
          variant="main"
          description={
            loading || (loading === false && !description) ? (
              <SummaryLoading />
            ) : (
              description
            )
          }
        />
      </Container>

      <Container size="marketing">
        {!loading && categories.length > 0 && (
          <FiltersContent
            categories={categories}
            selectedCategories={selectedCategories}
            onCategoryToggle={handleCategoryToggle}
            getCategoryCount={getCategoryCount}
            justify="center"
            disabled={selectedRoute !== null}
          />
        )}

        <div className={mergeClasses(mapContainer, className)}>
          <SSRWrapper>
            {!isMapLoaded && webGLSupported && (
              <ProgressBar position="absolute" variant="infinite" />
            )}
          </SSRWrapper>

          <MapboxMapView
            ref={mapInstanceRef}
            center={center}
            zoom={zoom}
            geoJSON={circleGeoJSON}
            accessToken={accessToken}
            onReady={handleReady}
            onViewportChange={handleViewportChange}
            hotspots={isMapIntersecting ? clusteredHotspots : undefined}
            controlsConfig={controlsConfig}
            customControlsTrailing={
              <Tooltip value="Kaart vergroten">
                <IconButton
                  variant="secondary"
                  onClick={onViewAll}
                  icon="fullScreen"
                  size="xs"
                  style={{ pointerEvents: "auto" }}
                />
              </Tooltip>
            }
            hideLabels
          />
        </div>
      </Container>

      {!loading && amenities && amenities.length > 0 && (
        <Container size={container}>
          <Grid variant="threeColumn" className={amenitiesGridStyle}>
            {(selectedCategories.length === 0
              ? oneAmenityPerCategory
              : amenities
                  .filter(
                    (amenity) =>
                      amenity.category &&
                      selectedCategories.includes(amenity.category),
                  )
                  .slice(0, 6)
            ).map((amenity, index) => (
              <AmenityCard
                key={index}
                icon={amenity.icon ?? "check"}
                name={amenity.name}
                distance={amenity.distance}
                type={amenity.type}
                padding="none"
                variant="opaque"
              />
            ))}
          </Grid>

          {onViewAll && (
            <Flex justify="center">
              <Button
                onClick={onViewAll}
                leading={<Icon icon="pin" size="s2" />}
              >
                Bekijk alle voorzieningen
              </Button>
            </Flex>
          )}
        </Container>
      )}

      {index !== undefined && (
        <Text align="center">
          {padNumber(index)} &ndash; {title}
        </Text>
      )}
      <Spacing />
    </Section>
  );
};
